import { Avatar, ReplyIcon, cn } from '@playerbase/ui';

import { FeedbackRow } from './feedback-row.js';
import type { InboxGroup, InboxMessage } from './use-feedback-inbox.js';

// ---------------------------------------------------------------------------
// Discord-style forum post.
//
// Forum threads in feedback_pool are bucketed by thread_id and arrive
// here as an InboxGroup with kind: 'forum-post'. We render the OP as
// a clickable card (title + author + body preview + reply summary)
// rather than the inline chat row used elsewhere - this matches the
// browse experience of a Discord forum channel and keeps the feed
// scannable when a forum has many active posts. Clicking the card
// expands it into the full conversation history, where each message
// follows the same chat-row rendering rules as the rest of the feed.
// ---------------------------------------------------------------------------

interface ForumPostCardProps {
  group: InboxGroup;
  byExternalId: Map<string, InboxMessage>;
  open: boolean;
  onToggle: () => void;
  controlsId: string;
  // Activity-panel click flow: when one of the inner FeedbackRows
  // matches highlightRowId, that row pulses with a ring. The card
  // forwards both the id and the per-row ref registrar straight
  // through; it does not need to know which row will match.
  highlightRowId?: string | null | undefined;
  registerRowRef?:
    | ((rowId: string, el: HTMLDivElement | null) => void)
    | undefined;
}

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

function relativeTime(timestamp: string): string {
  const date = new Date(timestamp);
  const diff = Date.now() - date.getTime();
  if (diff < 60_000) return 'just now';
  if (diff < ONE_DAY_MS) {
    const minutes = Math.floor(diff / 60_000);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  }
  const days = Math.floor(diff / ONE_DAY_MS);
  if (days < 30) return `${days}d ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function trimToPreview(text: string): string {
  const trimmed = text.trim();
  if (trimmed.length === 0) return '(no content)';
  if (trimmed.length > 240) return `${trimmed.slice(0, 240).trimEnd()}…`;
  return trimmed;
}

function bundleText(messages: InboxMessage[]): string {
  return messages
    .map((m) => m.rawText.trim())
    .filter((s) => s.length > 0)
    .join(' ');
}

interface CardPreview {
  // Author handle to prefix the preview body with. Null when the
  // latest activity is the OP - their handle already sits at the top
  // of the card so re-stating it would be noise.
  authorPrefix: string | null;
  body: string;
}

// Pick the most recent message across head + replies. When that
// message is the OP, render the OP body (with no author prefix).
// When it's a reply, render the reply body prefixed with the reply
// author so a viewer browsing the forum tile list sees what just
// happened on this thread without having to open it.
function latestPreview(group: InboxGroup): CardPreview {
  let latestPostedAt: string | null = null;
  let latestRow: { row: typeof group.head; isHead: boolean } | null = null;
  for (const m of group.head.messages) {
    if (m.postedAt === null) continue;
    if (latestPostedAt === null || m.postedAt > latestPostedAt) {
      latestPostedAt = m.postedAt;
      latestRow = { row: group.head, isHead: true };
    }
  }
  for (const r of group.replies) {
    for (const m of r.messages) {
      if (m.postedAt === null) continue;
      if (latestPostedAt === null || m.postedAt > latestPostedAt) {
        latestPostedAt = m.postedAt;
        latestRow = { row: r, isHead: false };
      }
    }
  }
  if (latestRow === null) {
    return { authorPrefix: null, body: trimToPreview(bundleText(group.head.messages)) };
  }
  if (latestRow.isHead) {
    return { authorPrefix: null, body: trimToPreview(bundleText(group.head.messages)) };
  }
  return {
    authorPrefix: latestRow.row.authorHandle ?? 'unknown',
    body: trimToPreview(bundleText(latestRow.row.messages)),
  };
}

function lastReplyTimestamp(group: InboxGroup): string | null {
  let latest: string | null = null;
  for (const r of group.replies) {
    for (const m of r.messages) {
      if (m.postedAt === null) continue;
      if (latest === null || m.postedAt > latest) latest = m.postedAt;
    }
  }
  return latest;
}

export function ForumPostCard({
  group,
  byExternalId,
  open,
  onToggle,
  controlsId,
  highlightRowId = null,
  registerRowRef,
}: ForumPostCardProps): JSX.Element {
  const head = group.head;
  const title = head.discordChannelName ?? '(untitled thread)';
  const forumName = head.discordParentChannelName;
  const author = head.authorHandle ?? 'unknown';
  const headPostedAt = head.messages[0]?.postedAt ?? null;
  // Count every visible message bundle in the thread, not just the
  // ones grouped under `replies`. The head IS one of the messages in
  // the thread - in starterMissing it's literally a reply that got
  // promoted, and in the normal forum-post case it's the OP, which
  // users count as the first message ("1 reply") regardless. Counting
  // bundles keeps the label honest with what's on screen.
  const replyCount = 1 + group.replies.length;
  const replyNoun = replyCount === 1 ? 'reply' : 'replies';
  const lastReply = lastReplyTimestamp(group);
  const preview = latestPreview(group);
  // Expanded thread reads top-down newest-first by post time. Order
  // is stable: a reaction on an older message updates its pill in
  // place rather than bumping the bundle. Mirrors the chronological
  // ordering of the channel feed.
  const orderedMessages = [head, ...group.replies]
    .slice()
    .sort((a, b) =>
      (b.messages[0]?.postedAt ?? '').localeCompare(a.messages[0]?.postedAt ?? ''),
    );

  return (
    <div className="px-4 py-3">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={controlsId}
        className={cn(
          'block w-full rounded-md border border-border bg-surface p-3 text-left transition-colors',
          'hover:border-border-strong hover:bg-surface-2',
        )}
      >
        <div className="flex items-center gap-2 text-xs text-text-muted">
          <Avatar size="sm" name={author} />
          <span className="font-semibold text-text">{author}</span>
          {forumName !== null && <span>· #{forumName}</span>}
          {headPostedAt !== null && (
            <span className="ml-auto whitespace-nowrap">
              {relativeTime(headPostedAt)}
            </span>
          )}
        </div>
        <h3 className="mt-2 text-sm font-semibold text-text break-words">{title}</h3>
        <p className="mt-1 text-sm text-text-muted whitespace-pre-wrap break-words line-clamp-2">
          {preview.authorPrefix !== null && (
            <span className="font-semibold text-text">{preview.authorPrefix}: </span>
          )}
          {preview.body}
        </p>
        <div className="mt-2 flex items-center gap-2 text-xs text-text-muted">
          <span className="inline-flex items-center gap-1">
            <ReplyIcon className="h-3.5 w-3.5" />
            {replyCount} {replyNoun}
          </span>
          {lastReply !== null && replyCount > 0 && (
            <span>· last {relativeTime(lastReply)}</span>
          )}
          <span className="ml-auto font-medium text-text">
            {open ? 'Close' : 'Open'}
          </span>
        </div>
      </button>
      {/* Same grid-template-rows trick as FeedbackGroup. The mt-2
          spacing and rounded-md frame both belong to the expanded
          state, so they live on the inner content wrapper - the outer
          grid shell collapses to zero height when closed and leaves
          no gap above. */}
      <div
        id={controlsId}
        className={cn(
          'grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:duration-0',
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
        aria-hidden={!open}
      >
        <div className="overflow-hidden">
          {/* Indent + left-rule connector + tinted inner surface, mirroring
              the related-items treatment in the playtest session timeline
              (SessionResponseCard) so the thread conversation reads as a
              child of the OP card above rather than a peer of it. */}
          <div className="ml-6 mt-2 border-l-2 border-border pl-3">
            <div className="rounded-md border border-border bg-surface-2 divide-y divide-border">
              {orderedMessages.map((r) => (
                <FeedbackRow
                  key={r.id}
                  row={r}
                  byExternalId={byExternalId}
                  highlighted={highlightRowId === r.id}
                  innerRef={
                    registerRowRef !== undefined
                      ? (el) => registerRowRef(r.id, el)
                      : undefined
                  }
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
